import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { formatCurrency } from '@/utils/common/formatting';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { useResponsive } from '@/hooks/common/useResponsive';

interface Props {
  leaseId: number;
  paymentsBlocked?: boolean;
  isOverdue: boolean;
  inCredit: boolean;
  balanceDue: number;
  autopayEnabled: boolean;
}

export const PaymentContent: React.FC<Props> = ({
  leaseId,
  paymentsBlocked,
  isOverdue,
  inCredit,
  balanceDue,
  autopayEnabled,
}) => {
  const { layoutMode } = useResponsive();
  const isMobile = layoutMode !== 'desktop';
  
  const shouldPay = useMemo(() => {
    return ((balanceDue || 0) > 0 && !inCredit) || isOverdue;
  }, [balanceDue, inCredit, isOverdue]);
  
  const tooltip = useMemo(() => {
    if (isOverdue) {
      return 'The late fees for this account will continue to occur until the balance gets paid.';
    }
    if (inCredit) {
      return 'The credit will be applied automatically to fully or partially cover any outstanding charges that you have.';
    }
    return 'The balance includes both the rent and utility charges, if there are any.';
  }, [isOverdue, inCredit]);

  if (paymentsBlocked) return null;

  const styles = isMobile ? mobileStyles : desktopStyles;
  const colors = isMobile ? RESIDENTS_MOBILE_COLORS : RESIDENTS_DESKTOP_COLORS;

  return (
    <View style={styles.container}>
      <View style={styles.balanceRow}>
        <Text style={[
          styles.balance, 
          isOverdue && styles.balanceOverdue, 
          inCredit && styles.balanceCredit
        ]}>
          {formatCurrency(balanceDue)}
        </Text>
        <InfoIcon tooltip={tooltip} isMobile={isMobile} />
      </View>
      <Text style={styles.balanceMsg}>{shouldPay ? 'Balance due' : 'No balance due.'}</Text>
      {autopayEnabled && (
        <View style={styles.autopayRow}>
          <CheckIcon />
          <Text style={styles.autopayText}>Autopay active</Text>
        </View>
      )}
    </View>
  );
};

const InfoIcon: React.FC<{ tooltip: string; isMobile: boolean }> = ({ tooltip, isMobile }) => {
  const Icon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE as any;
  const colors = isMobile ? RESIDENTS_MOBILE_COLORS : RESIDENTS_DESKTOP_COLORS;
  if (!Icon) return null;
  
  if (Platform.OS !== 'web' || isMobile) {
    // On mobile or native, just show the icon with accessibility
    return (
      <View accessibilityLabel={tooltip} accessible style={mobileStyles.infoIconWrap}>
        <Icon width={16} height={16} color={colors.TEXT_TERTIARY} />
      </View>
    );
  }
  
  // On desktop web, use DesktopOverflowText with SVG icon
  return (
    <View style={desktopStyles.infoIconWrap}>
      <DesktopOverflowText
        text=""
        title=""
        tooltipText={tooltip}
        numberOfLines={1}
        icon={<Icon width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.ICON_ACTIVE} />}
      />
    </View>
  );
};

const CheckIcon: React.FC = () => {
  const Icon = RESIDENTS_SVG_COMPONENTS.CHECK as any;
  if (!Icon) return null;
  return (
    <View style={mobileStyles.checkIconWrap}>
      <Icon width={16} height={16} color={'#10B981'} />
    </View>
  );
};

// Mobile styles
const mobileStyles = StyleSheet.create({
  container: {
    paddingHorizontal: 0,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  infoIconWrap: {
    marginLeft: 6,
    opacity: 0.6,
  },
  balance: {
    fontSize: 20,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
  },
  balanceOverdue: {
    color: '#EF4444',
  },
  balanceCredit: {
    color: '#10B981',
  },
  balanceMsg: {
    fontSize: 12,
    color: RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY,
    marginBottom: 8,
  },
  autopayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  checkIconWrap: {
    marginRight: 4,
  },
  autopayText: {
    fontSize: 11,
    color: '#10B981',
    fontWeight: '600',
  },
});

// Desktop styles
const desktopStyles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  infoIconWrap: {
    marginLeft: 8,
    opacity: 0.6,
  },
  balance: {
    fontSize: 24,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  balanceOverdue: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  balanceCredit: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SUCCESS,
  },
  balanceMsg: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    marginBottom: 12,
  },
  autopayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  checkIconWrap: {
    marginRight: 6,
  },
  autopayText: {
    fontSize: 12,
    color: '#10B981',
  },
});



